import { z } from 'zod';
import { compararPercentual, somarPercentual } from './aritmetica.ts';
import {
  acaoSaldoTituloSchema,
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  paginacaoSchema,
  percentualSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';
import {
  MAXIMO_RATEIOS_TITULO,
  MENSAGEM_PARCELA_MAIOR_QUE_TOTAL,
  indiceRateioSchema,
  marcarRepetidos,
  numeroDaParcelaSchema,
  numeroDocumentoSchema,
  parcelaCabeNoTotal,
  rateioDeObraCabe,
  rateioDoTituloEntradaSchema,
  rateioDoTituloSchema,
  situacaoTituloSchema,
  valorAcessorioSchema,
  valorLiquidoDaBaixa,
  type SituacaoTitulo,
} from './titulos.ts';

/**
 * Contas a pagar — o título e a baixa dele (`.spec/010`, módulo Pagamento;
 * `.spec/016`, seção 10).
 *
 * O módulo tem duas portas de entrada muito diferentes, e quase tudo aqui
 * decorre disso:
 *
 * - **Derivada.** A confirmação da nota de compra transforma cada parcela do
 *   financeiro dela num título, com o rateio de obra e a classificação contábil
 *   já calculados a partir dos itens. Esse título não se digita nem se corrige
 *   por aqui: quem o criou foi a nota, e quem o desfaz é a exclusão dela.
 * - **Derivada do contrato.** O contrato de pagamento — a empreitada global, a
 *   obra tocada por terceiro — gera um título por parcela da condição de
 *   pagamento. Vale para ele tudo o que vale para o título de nota: não se
 *   digita nem se corrige por aqui, e quem o desfaz é a edição do contrato.
 * - **Manual.** Existe dívida que não nasce de documento — a conta de energia da
 *   obra, o honorário do escritório, o acerto combinado com o fornecedor. A
 *   `.spec/016`, seção 10, prevê isso ao escrever `num_nonnulls(...) <= 1` em
 *   vez de `= 1`: o título **sem** origem é legítimo.
 *
 * A tela é uma só; o que muda é o que ela deixa fazer. `tituloEditavel`
 * (em `titulos.ts`) é a função que decide, e ela vive lá porque a API, o
 * formulário e o contas a receber precisam da **mesma** resposta — a tela que
 * habilitasse o botão que o servidor recusa seria pior que não ter botão.
 */

/* -------------------------------------------------------------------------- */
/* Origem e situação                                                          */
/* -------------------------------------------------------------------------- */

/**
 * De onde o título veio.
 *
 * É **derivada** das colunas de origem, não gravada: `contas_pagar` tem
 * `nota_compra_id`, `pagamento_mao_obra_id` e `contrato_id`, mutuamente
 * exclusivas por `CHECK`, e uma quarta coluna com o código repetiria em texto o
 * que as três já dizem — com a chance de discordar delas.
 */
export const ORIGENS_TITULO = [
  'NOTA_COMPRA',
  'MAO_OBRA',
  'CONTRATO',
  /** O título que nasceu de um agrupamento de títulos (decisão 119). */
  'AGRUPAMENTO',
  'MANUAL',
] as const;
export const origemTituloSchema = z.enum(ORIGENS_TITULO);
export type OrigemTitulo = z.infer<typeof origemTituloSchema>;

export const ROTULO_ORIGEM_TITULO: Readonly<Record<OrigemTitulo, string>> = {
  NOTA_COMPRA: 'Nota de compra',
  MAO_OBRA: 'Pagamento de mão de obra',
  CONTRATO: 'Contrato de pagamento',
  AGRUPAMENTO: 'Agrupamento de títulos',
  MANUAL: 'Lançamento manual',
};

/**
 * Rótulos da situação — o enum e o cálculo estão em `titulos.ts`, que é onde o
 * contas a receber os compartilha. Só o texto é daqui: no receber, `QUITADO` se
 * lê "Recebido".
 */
export const ROTULO_SITUACAO_TITULO: Readonly<Record<SituacaoTitulo, string>> = {
  EM_ABERTO: 'Em aberto',
  PARCIAL: 'Baixado em parte',
  QUITADO: 'Quitado',
};

/*
  `tituloEditavel` mudou de arquivo, e não de regra: está em `titulos.ts`, junto
  do resto do que os dois módulos compartilham. Ela ficou aqui enquanto só o
  pagar tinha origem; o contrato deu origem ao receber também, e manter duas
  cópias da mesma linha era esperar que a primeira correção alcançasse só uma.

  O que **todo** título a pagar aceita, tenha a origem que tiver, é reprogramar
  vencimento, previsão e observação (`reprogramarContaPagarSchema`): renegociar
  prazo com o fornecedor não altera o que se deve, e obrigar a mexer na nota
  fiscal ou no contrato por causa disso seria pior.
*/

export const MAXIMO_CLASSIFICACOES_TITULO = 50;

/* -------------------------------------------------------------------------- */
/* Filhos: rateio de obra e classificação contábil                            */
/* -------------------------------------------------------------------------- */

/**
 * Em que conta contábil o título se classifica, e em que proporção.
 *
 * Existe no lugar de uma coluna `conta_contabil_id` no título — a `.spec/016`,
 * seção 10, previa a coluna, e a divergência está explicada no schema. O motivo
 * curto: uma parcela cobre itens de naturezas diferentes (cimento, furadeira e
 * serviço de guindaste na mesma nota), e com uma coluna só a nota inteira
 * apareceria na conta da primeira linha — que é o número que o balancete lê.
 *
 * Aqui a soma **é** exatamente 100%: dívida com 40% sem natureza contábil não é
 * algo que balancete nenhum saiba apresentar.
 */
export const classificacaoDoTituloEntradaSchema = z.object({
  contaContabilId: uuidSchema,
  indiceRateio: indiceRateioSchema,
});

export type ClassificacaoDoTituloEntrada = z.infer<typeof classificacaoDoTituloEntradaSchema>;

export const classificacaoDoTituloSchema = z.object({
  id: uuidSchema,
  contaContabilId: uuidSchema,
  contaContabilCodigo: z.string().nullable(),
  contaContabilDescricao: z.string().nullable(),
  indiceRateio: percentualSchema,
});

export type ClassificacaoDoTitulo = z.infer<typeof classificacaoDoTituloSchema>;

/** A classificação contábil fecha em 100%? Regra compartilhada com a tela. */
export function classificacaoFechaEmCem(
  classificacoes: readonly { indiceRateio: string }[],
): boolean {
  if (classificacoes.length === 0) return false;

  return (
    compararPercentual(somarPercentual(classificacoes.map((c) => c.indiceRateio)), '100') === 0
  );
}

/* -------------------------------------------------------------------------- */
/* Título                                                                     */
/* -------------------------------------------------------------------------- */

const valorDoTituloSchema = dinheiroSchema.refine(
  (v) => Number(v) > 0,
  'O valor do título deve ser maior que zero.',
);

const rateiosSchema = z
  .array(rateioDoTituloEntradaSchema)
  .max(MAXIMO_RATEIOS_TITULO, `O título tem no máximo ${MAXIMO_RATEIOS_TITULO} rateios.`)
  .prefault([]);

const classificacoesSchema = z
  .array(classificacaoDoTituloEntradaSchema)
  .min(1, 'Informe ao menos uma conta contábil para o título.')
  .max(
    MAXIMO_CLASSIFICACOES_TITULO,
    `O título tem no máximo ${MAXIMO_CLASSIFICACOES_TITULO} classificações.`,
  );

/**
 * Payload do título **manual**.
 *
 * O que ele deliberadamente **não** tem, e por quê:
 *
 * - `valorSaldo` — nasce igual ao valor e depois só a baixa o move. Aceitá-lo
 *   deixaria um título se declarar meio pago sem que nenhuma baixa existisse, e
 *   o extrato da conta corrente não teria a contrapartida.
 * - `notaCompraId`, `notaCompraFinanceiroId`, `pagamentoMaoObraId`,
 *   `contratoId`, `agrupamentoId` — origem não se digita. Um título que se dissesse filho de uma
 *   nota sem ter saído da confirmação dela apareceria na reversão da nota e
 *   sumiria com ela; um que se dissesse filho de um contrato seria apagado pela
 *   primeira edição dele.
 * - `situacao` — é leitura do saldo (ver `situacaoDoTitulo`).
 */
export const criarContaPagarSchema = z
  .object({
    /** Empresa do grupo que deve — `pessoas` com `is_empresa`. */
    empresaId: uuidSchema,
    /** Dia em que o título entra no sistema. Data de negócio, sem hora. */
    dataLancamento: dataDoFatoSchema,
    /** A quem se deve — `pessoas` com `is_fornecedor`. */
    fornecedorId: uuidSchema,
    /**
     * O documento que originou a dívida — boleto, guia, recibo.
     *
     * Obrigatório também aqui, no título digitado: é por ele que a conciliação
     * com o extrato acontece. No título gerado pela nota de compra quem o
     * preenche é a confirmação, com o número da nota **sem a série**.
     */
    numeroDocumento: numeroDocumentoSchema,
    /**
     * A parcela do documento e quantas ele tem — o "2/3" da tela.
     *
     * Aceita-se no manual porque a conta de energia parcelada e o acerto
     * combinado em três vezes existem, e o operador lança um título por
     * parcela: sem os campos, as três linhas ficariam indistinguíveis a não ser
     * pelo vencimento. O padrão `1` faz o título avulso nascer `1/1` sem que
     * ninguém tenha de pensar no assunto.
     *
     * No título **derivado** quem os preenche é a origem — a confirmação da
     * nota e a geração do contrato —, e este payload não os alcança.
     */
    parcela: numeroDaParcelaSchema,
    totalParcelas: numeroDaParcelaSchema,
    dataVencimento: dataSchema,
    /**
     * Quando se pretende pagar. Nulo recebe o vencimento (`.spec/016`, seção
     * 10) — a cópia é feita por quem grava, e não por `coalesce` na consulta:
     * o fluxo de caixa projetado lê esta data, e a primeira consulta escrita
     * sem o `coalesce` esconderia o título do relatório.
     */
    dataPrevisaoPagamento: dataSchema.nullable().prefault(null),
    valor: valorDoTituloSchema,
    observacao: z.string().trim().max(500).prefault(''),
    rateio: rateiosSchema,
    classificacoes: classificacoesSchema,
  })
  .superRefine((dados, ctx) => {
    if (dados.dataPrevisaoPagamento && dados.dataPrevisaoPagamento < dados.dataLancamento) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dataPrevisaoPagamento'],
        message: 'A previsão de pagamento não pode ser anterior ao lançamento do título.',
      });
    }

    if (!parcelaCabeNoTotal(dados)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['parcela'],
        message: MENSAGEM_PARCELA_MAIOR_QUE_TOTAL,
      });
    }

    if (!classificacaoFechaEmCem(dados.classificacoes)) {
      const soma = somarPercentual(dados.classificacoes.map((c) => c.indiceRateio));
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['classificacoes'],
        message: `A classificação contábil soma ${soma}% e precisa somar exatamente 100%.`,
      });
    }

    if (!rateioDeObraCabe(dados.rateio)) {
      const soma = somarPercentual(dados.rateio.map((r) => r.indiceRateio));
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rateio'],
        message: `O rateio de obra soma ${soma}% e não pode passar de 100%. A parte que sobra fica sem obra, como compra de almoxarifado.`,
      });
    }

    // A mesma obra duas vezes é digitação repetida — e o índice único da tabela
    // recusaria a segunda linha. Pegar aqui devolve o erro preso à linha errada,
    // e não uma violação de constraint que a tela não sabe onde mostrar.
    marcarRepetidos(
      dados.rateio.map((r) => `${r.obraId}|${r.obraParteId ?? ''}`),
      'rateio',
      'obraId',
      'Esta obra já está no rateio. Some os percentuais numa fatia só.',
      ctx,
    );

    marcarRepetidos(
      dados.classificacoes.map((c) => c.contaContabilId),
      'classificacoes',
      'contaContabilId',
      'Esta conta contábil já está na classificação. Some os percentuais numa fatia só.',
      ctx,
    );
  });

export type CriarContaPagar = z.infer<typeof criarContaPagarSchema>;
export const atualizarContaPagarSchema = criarContaPagarSchema;
export type AtualizarContaPagar = z.infer<typeof atualizarContaPagarSchema>;

/**
 * Reprogramação: as três coisas que **qualquer** título aceita mudar.
 *
 * Vale para o derivado também, e é o único caminho de escrita que ele tem.
 * Renegociar prazo com o fornecedor não muda o que se deve nem de onde a dívida
 * veio; exigir que se mexa na nota fiscal por causa de um vencimento adiado
 * levaria alguém a mexer na nota fiscal por causa de um vencimento adiado.
 */
export const reprogramarContaPagarSchema = z.object({
  dataVencimento: dataSchema,
  dataPrevisaoPagamento: dataSchema.nullable().prefault(null),
  observacao: z.string().trim().max(500).prefault(''),
});

export type ReprogramarContaPagar = z.infer<typeof reprogramarContaPagarSchema>;

/** Título como a API o devolve, com os vínculos resolvidos e o saldo. */
export const contaPagarSchema = z.object({
  id: uuidSchema,
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  fornecedorId: uuidSchema,
  /** Nome de uso: fantasia quando há, razão social quando não (`nomeDeUso`). */
  fornecedorNome: z.string().nullable(),
  origem: origemTituloSchema,
  /** Nulo fora da origem `NOTA_COMPRA`. */
  notaCompraId: uuidSchema.nullable(),
  /**
   * Chave da NF-e da nota de origem — o que a tela mostra para dizer "este
   * título veio daquele documento". A nota não tem número próprio no schema; a
   * chave é o identificador que o fornecedor e o fisco reconhecem.
   */
  notaCompraChaveNfe: z.string().nullable(),
  /** Nulo fora da origem `CONTRATO`. */
  contratoId: uuidSchema.nullable(),
  /**
   * Número do contrato de origem — o que a tela mostra para dizer "este título
   * veio daquele contrato". É o número por extenso, com os 60 caracteres que o
   * cadastro admite, e não o `numeroDocumento` do título, que tem 30.
   */
  contratoNumero: z.string().nullable(),
  /** Nulo fora da origem `AGRUPAMENTO`. */
  agrupamentoId: uuidSchema.nullable(),
  /** Número do agrupamento de origem — o que se cita ao fornecedor. */
  agrupamentoNumero: z.number().int().nullable(),
  /**
   * O documento que originou a dívida. No título vindo de nota é o número dela,
   * sem a série — e é o que a listagem mostra, porque a chave da NF-e tem 44
   * dígitos e não cabe numa coluna que se lê de relance.
   */
  numeroDocumento: z.string(),
  /**
   * A parcela e o total, separados — a tela é que os junta em "2/3".
   *
   * Devolver o texto pronto pouparia uma linha na tela e custaria a ordenação e
   * o filtro, que precisam do número.
   */
  parcela: z.number().int(),
  totalParcelas: z.number().int(),
  dataLancamento: dataSchema,
  dataVencimento: dataSchema,
  dataPrevisaoPagamento: dataSchema,
  valor: dinheiroSchema,
  valorSaldo: dinheiroSchema,
  /** Derivada do saldo — ver `situacaoDoTitulo`. */
  situacao: situacaoTituloSchema,
  /** `true` quando o título aceita edição de valor e vínculos — só o manual. */
  editavel: z.boolean(),
  observacao: z.string(),
  rateio: z.array(rateioDoTituloSchema),
  classificacoes: z.array(classificacaoDoTituloSchema),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type ContaPagar = z.infer<typeof contaPagarSchema>;

/**
 * Listagem: a página **mais os totais do recorte inteiro**.
 *
 * Os totais não saem da soma da página — somariam 50 linhas de um filtro com
 * 900 — e por isso não podem ser calculados no cliente. "Quanto vence esta
 * semana" é a pergunta que abre a tela, e ela é sobre o filtro, não sobre o que
 * coube na primeira página.
 */
export const listaContasPagarSchema = respostaListaSchema(contaPagarSchema).extend({
  totalValor: dinheiroSchema,
  totalSaldo: dinheiroSchema,
});

export type ListaContasPagar = z.infer<typeof listaContasPagarSchema>;

/**
 * Filtros.
 *
 * Sem `ativo`: título é documento de movimento, não cadastro. O recorte natural
 * é **fornecedor e vencimento** — é assim que se procura uma dívida, e é o
 * índice que a tabela tem (`contas_pagar_fornecedor`,
 * `contas_pagar_vencimento_em_aberto`).
 */
export const listarContasPagarQuerySchema = paginacaoSchema.extend({
  empresaId: uuidSchema.optional(),
  fornecedorId: uuidSchema.optional(),
  /** Recorta pelas fatias do rateio: "quanto se deve pela Residencial Aurora". */
  obraId: uuidSchema.optional(),
  origem: origemTituloSchema.optional(),
  /** "Quais títulos este contrato gerou" — o caminho de volta a partir dele. */
  contratoId: uuidSchema.optional(),
  /**
   * Situação, **como recorte e não como rótulo**.
   *
   * `EM_ABERTO` aqui traz tudo com `valor_saldo > 0`, incluindo o parcialmente
   * baixado: quem pergunta o que ainda se deve inclui o título de dez mil com
   * três mil pagos. `PARCIAL` continua isolando só esses, e os dois se
   * sobrepõem de propósito. Ver `filtroDeSituacao`, no repositório.
   */
  situacao: situacaoTituloSchema.optional(),
  /** Vencimento — início e fim, inclusivos. */
  vencimentoDe: dataSchema.optional(),
  vencimentoAte: dataSchema.optional(),
  /** Previsão de pagamento — o recorte do fluxo de caixa projetado. */
  previsaoDe: dataSchema.optional(),
  previsaoAte: dataSchema.optional(),
});

export type ListarContasPagarQuery = z.infer<typeof listarContasPagarQuerySchema>;

export const COLUNAS_ORDENAVEIS_CONTA_PAGAR = [
  'dataVencimento',
  'dataPrevisaoPagamento',
  'dataLancamento',
  'valor',
  'valorSaldo',
  'createdAt',
] as const;

/* -------------------------------------------------------------------------- */
/* Baixa                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Payload da baixa.
 *
 * O que ele **não** tem:
 *
 * - `acaoSaldoTitulo` — é copiada do histórico financeiro escolhido, no momento
 *   da baixa (`.spec/016`, seção 10). Deixar o cliente mandá-la permitiria
 *   quitar um título com um histórico que aumenta saldo, e a cópia perderia o
 *   sentido de ser cópia.
 * - `valorLiquido` — é conta, não dado (ver `valorLiquidoDaBaixa`). Aceitá-lo
 *   deixaria o extrato bancário e o título discordarem por um número digitado.
 * - `contaPagarId` — vem da URL. No corpo, um id diferente do da rota daria duas
 *   respostas para "qual título estou baixando".
 */
export const criarBaixaContaPagarSchema = z
  .object({
    /** Dia em que o dinheiro saiu. Data de negócio, sem hora. */
    dataMovimentacao: dataDoFatoSchema,
    /** Conta corrente de onde o dinheiro sai — o saldo dela anda nesta gravação. */
    contaCorrenteId: uuidSchema,
    /**
     * Forma de pagamento, e a **única escolha de histórico** da baixa.
     *
     * Dela saem duas coisas: a ação sobre o saldo do título e o histórico
     * bancário das movimentações. O bancário já é atributo da forma de pagamento
     * no cadastro — pedi-lo de novo aqui criava duas respostas para a mesma
     * pergunta, sem nada obrigando as duas a concordarem. A baixa é recusada
     * quando a forma escolhida não tem o vínculo.
     *
     * A conta contábil também saiu do payload: o principal é rateado pela
     * classificação contábil do título, e não existe mais *uma* conta da baixa.
     * Escolher uma entre N classificava o pagamento inteiro numa natureza que
     * podia ser 51% dele.
     */
    historicoFinanceiroId: uuidSchema,
    /** O que a baixa abate do saldo do título. Move o principal na conta corrente. */
    valorBaixaSaldoTitulo: dinheiroSchema.refine(
      (v) => Number(v) > 0,
      'O valor da baixa deve ser maior que zero.',
    ),
    valorJuros: valorAcessorioSchema,
    valorOutros: valorAcessorioSchema,
    valorDesconto: valorAcessorioSchema,
    observacao: z.string().trim().max(500).prefault(''),
  })
  .superRefine((dados, ctx) => {
    // O líquido é o que sai do banco. Zero ou negativo significaria pagamento
    // que não movimenta ou que devolve dinheiro — e nenhum dos dois é uma baixa;
    // o segundo é um estorno, que tem caminho próprio.
    if (Number(valorLiquidoDaBaixa(dados)) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['valorDesconto'],
        message:
          'O desconto zera ou inverte o valor líquido da baixa. O líquido é o que sai do banco e precisa ser maior que zero.',
      });
    }
  });

export type CriarBaixaContaPagar = z.infer<typeof criarBaixaContaPagarSchema>;

export const baixaContaPagarSchema = z.object({
  id: uuidSchema,
  contaPagarId: uuidSchema,
  dataMovimentacao: dataSchema,
  contaCorrenteId: uuidSchema,
  contaCorrenteDescricao: z.string().nullable(),
  historicoFinanceiroId: uuidSchema,
  historicoFinanceiroDescricao: z.string().nullable(),
  /** Cópia congelada da ação do histórico no momento da baixa. */
  acaoSaldoTitulo: acaoSaldoTituloSchema,
  valorBaixaSaldoTitulo: dinheiroSchema,
  valorJuros: dinheiroSchema,
  valorOutros: dinheiroSchema,
  valorDesconto: dinheiroSchema,
  valorLiquido: dinheiroSchema,
  /** Nulo enquanto a baixa vale. Preenchido, ela consta como estornada. */
  estornadaEm: dataHoraSchema.nullable(),
  estornadaPorNome: z.string().nullable(),
  motivoEstorno: z.string().nullable(),
  observacao: z.string(),
  createdAt: dataHoraSchema,
});

export type BaixaContaPagar = z.infer<typeof baixaContaPagarSchema>;

export const listaBaixasContaPagarSchema = respostaListaSchema(baixaContaPagarSchema);
export type ListaBaixasContaPagar = z.infer<typeof listaBaixasContaPagarSchema>;

/** O rótulo é neutro e mora em `titulos.ts`; a dica fala de dívida e é daqui. */
export const DICA_ACAO_SALDO_TITULO: Readonly<
  Record<z.infer<typeof acaoSaldoTituloSchema>, string>
> = {
  DIMINUI_SALDO: 'O valor da baixa abate o que ainda se deve.',
  AUMENTA_SALDO:
    'O valor da baixa soma ao que se deve — é o caso de juros e multa lançados como baixa.',
  MANTEM_SALDO: 'O dinheiro sai da conta corrente, mas o saldo do título não muda.',
};
